import React from "react";
import "./SWRowPDF.scoped.scss";
import pdfIcon from "media/icons/dls/pdf.svg";
import shareLatestPdfLinkIcon from "media/icons/dls/share.svg";
import { ISWSummaryItem, RCTypes, SWTypes, TemplateTypes } from "interfaces/sw/SWInterfaces";
import formatDate from "utilities/formatDate";
import { useDispatch } from "react-redux";
import { getPDF } from "store/swList/swListActions";
import SWTypeIcon from "components/sw/search/SWTypeIcon";
import { AuthFilter } from "auth/AuthFilter";
import { Roles } from "interfaces/user/UserInterfaces";
import useSelector from "store/useSelector";
import { showInfoToast } from "store/toast/toastActions";
import config from "config";
import SWTypeIconAI from "components/sw/search/SWTypeIconAI";
import EllipsesDropdown, { IEllipsesDropdownItem } from "components/common/EllipsesDropdown";
import { PdfTypes } from "interfaces/misc/miscInterfaces";

interface ISWRowProps {
    sw: ISWSummaryItem;
    checkedSW: boolean,
}

const SWRowPDF: React.FC<ISWRowProps> = ({ sw, checkedSW }) => {
    const currentUser = useSelector(store => store.auth.currentUser);
    const dispatch = useDispatch();

    const handleDownloadPDFClick = (hasLatestPublishedVersion: boolean | undefined) => {
        dispatch(
            getPDF({
                swId: sw.id,
                generateDefault: true,
                getLatestVersion: hasLatestPublishedVersion,
                swGuid: hasLatestPublishedVersion ? sw.guid : null
            })
        );
    }

    const handleDownloadChoosenPDFClick = (pdfType: string, hasLatestPublishedVersion: boolean | undefined) => {
        dispatch(
            getPDF({
                swId: sw.id,
                pdfType: pdfType,
                getLatestVersion: hasLatestPublishedVersion,
                swGuid: hasLatestPublishedVersion ? sw.guid : null
            })
        );
    }

    const isLatestVersionHigher = () => {
        return sw.latestPublishedVersion !== undefined ? sw.version < sw.latestPublishedVersion : false;
    }

    const copyLink = () => {
        navigator.clipboard.writeText(config.endpoints.links.latestPdfViewerLink.replace("{swGuid}", sw.guid));
        dispatch(showInfoToast("Link copied to clipboard"));
    }

    const copyLinkForChoosenType = (pdfType: string) => {
        navigator.clipboard.writeText(config.endpoints.links.latestPdfViewerLinkForChoosenType.replace("{swGuid}", sw.guid).replace("{pdfType}", pdfType));
        dispatch(showInfoToast("Link copied to clipboard"));
    }

    return (
        <>
            {sw.latestPublishedVersion !== undefined && sw.latestPublishedVersion > 0
                && sw.dataClassification === "Private"
                && (
                    <div className="row">
                        <div className="cell sw-name">
                            <div className="title-holder">
                                {sw.type === SWTypes.TLMSWI && sw.isAutoTranslated ?
                                    <SWTypeIconAI />
                                    : <SWTypeIcon type={sw.type} />
                                }
                                <div className="sw-title">
                                    {sw.title}
                                    <span className="description">
                                        {sw.description}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="cell execution-count">
                            <div className="swcount">
                                {sw.guid}
                            </div>
                        </div>
                        <div className="cell pub swcount">
                            {sw.latestPublishedCreatedOn &&
                                (
                                    <>
                                        {formatDate(sw.latestPublishedCreatedOn, false)}
                                        <br />(
                                        {sw.latestPublishedVersion
                                            ? `version ${sw.latestPublishedVersion}`
                                            : ""}
                                        )
                                    </>
                                )}
                        </div>
                        <div className="dropdown-button cell buttons">
                            {(() => {
                                const isAuth = AuthFilter.isLoggedIn()
                                    .hasAnyRoleIn([Roles.ReportViewer, Roles.Author])
                                    .includeSysAdmins()
                                    .isMatch(currentUser);
                                let itemList: (IEllipsesDropdownItem | undefined)[] = [];
                                if (isAuth) {
                                    itemList = itemList.concat([
                                        sw.type === SWTypes.SWI ? ({
                                            key: "Download Field PDF",
                                            label: "Download Field PDF",
                                            icon: pdfIcon,
                                            onClick: () => handleDownloadChoosenPDFClick(PdfTypes.Field, isLatestVersionHigher()),
                                        }) :
                                            {
                                                key: sw.type === SWTypes.MFGSWI ? "Full training PDF" : sw.type === SWTypes.TLMSWI ? "Full PDF" : "Download PDF",
                                                label: sw.type === SWTypes.MFGSWI ? "Full training PDF" : sw.type === SWTypes.TLMSWI ? "Full PDF" : "Download PDF",
                                                icon: pdfIcon,
                                                onClick: () => {
                                                    sw.type === SWTypes.MFGSWI ? handleDownloadChoosenPDFClick(PdfTypes.FullTraining, isLatestVersionHigher()) :
                                                        sw.type === SWTypes.TLMSWI ? handleDownloadChoosenPDFClick(PdfTypes.Full, isLatestVersionHigher()) :
                                                            handleDownloadChoosenPDFClick(PdfTypes.Normal, isLatestVersionHigher())
                                                },
                                            },
                                        (sw.type === SWTypes.MFGSWI || sw.type === SWTypes.MFGCL) ? ({
                                            key: "Full Training PDF (all steps)",
                                            label: "Full Training PDF (all steps)",
                                            icon: pdfIcon,
                                            onClick: () => handleDownloadChoosenPDFClick(PdfTypes.FullTrainingAllSteps, isLatestVersionHigher()),
                                        }) : undefined,
                                        (sw.type === SWTypes.SWI) ? ({
                                            key: "Download Full Training PDF",
                                            label: "Download Full Training PDF",
                                            icon: pdfIcon,
                                            onClick: () => handleDownloadChoosenPDFClick(PdfTypes.FullTraining, isLatestVersionHigher()),
                                        }) : undefined,
                                        (sw.type === SWTypes.TLMSWI) ? (
                                            {
                                                key: "Standard PDF",
                                                label: sw.templateType === TemplateTypes.Consolidated ? "Consolidated PDF" : "Standard PDF",
                                                icon: pdfIcon,
                                                onClick: () => {
                                                    sw.templateType === TemplateTypes.Consolidated ?
                                                        handleDownloadChoosenPDFClick(PdfTypes.Consolidated, isLatestVersionHigher()) :
                                                        handleDownloadChoosenPDFClick(PdfTypes.Standard, isLatestVersionHigher())
                                                },
                                            }) : undefined,
                                        (sw.type === SWTypes.MFGSWI) ? ({
                                            key: "Reference PDF",
                                            label: "Reference PDF",
                                            icon: pdfIcon,
                                            onClick: () => handleDownloadChoosenPDFClick(PdfTypes.Reference, isLatestVersionHigher()),
                                        }) : undefined,
                                        (sw.type === SWTypes.TLMSWI) ?
                                            {
                                                key: "Executable PDF",
                                                label: "Executable PDF",
                                                icon: pdfIcon,
                                                onClick: () => handleDownloadChoosenPDFClick(PdfTypes.Executable, isLatestVersionHigher()),
                                            } : undefined,
                                        (sw.type === SWTypes.CL ||
                                            sw.type === SWTypes.LCL ||
                                            sw.type === SWTypes.SWI ||
                                            (sw.type === SWTypes.TLMRC && sw.rcType !== RCTypes.Notice) ||
                                            sw.type === SWTypes.TLMSWI ||
                                            sw.type === SWTypes.MFGSWI ||
                                            sw.type === SWTypes.MFGCL ||
                                            (sw.type === SWTypes.MFGRC && sw.rcType !== RCTypes.Notice)
                                        ) ? ({
                                            key: sw.type === SWTypes.TLMSWI || sw.type === SWTypes.TLMRC || sw.type === SWTypes.MFGRC ? "Competency - CPA" : "CCQ PDF",
                                            label: sw.type === SWTypes.TLMSWI || sw.type === SWTypes.TLMRC || sw.type === SWTypes.MFGRC ? "Competency - CPA" : "CCQ PDF",
                                            icon: pdfIcon,
                                            onClick: () => {
                                                sw.type === SWTypes.TLMSWI ||
                                                    sw.type === SWTypes.TLMRC ||
                                                    sw.type === SWTypes.MFGRC ? handleDownloadChoosenPDFClick(PdfTypes.CompetencyCPA, isLatestVersionHigher()) : handleDownloadChoosenPDFClick(PdfTypes.CCQ, isLatestVersionHigher())
                                            },
                                        }) : undefined
                                    ])
                                    return (
                                        <EllipsesDropdown
                                            itemTextAlign="left"
                                            items={itemList}
                                        >
                                            <div>
                                                <img
                                                    src={pdfIcon}
                                                    className="icon-medium hover-image"
                                                    alt="Download PDF" />
                                            </div>
                                        </EllipsesDropdown>
                                    );
                                }
                            })()}
                        </div>
                        <div className="dropdown-button cell buttons">
                            {(() => {
                                const isAuth = AuthFilter.isLoggedIn()
                                    .hasAnyRoleIn([Roles.ReportViewer])
                                    .isMatch(currentUser);
                                let itemList: (IEllipsesDropdownItem | undefined)[] = [];
                                if (isAuth) {
                                    itemList = itemList.concat([
                                        sw.type === SWTypes.SWI ? ({
                                            key: "Copy Field PDF Link",
                                            label: "Copy Field PDF Link",
                                            icon: pdfIcon,
                                            onClick: () => copyLinkForChoosenType(PdfTypes.Field),
                                        }) :
                                            {
                                                key: sw.type === SWTypes.MFGSWI ? "Full training PDF" : sw.type === SWTypes.TLMSWI ? "Full PDF" : "Copy PDF Link",
                                                label: sw.type === SWTypes.MFGSWI ? "Full training PDF" : sw.type === SWTypes.TLMSWI ? "Full PDF" : "Copy PDF Link",
                                                icon: pdfIcon,
                                                onClick: () => {
                                                    sw.type === SWTypes.MFGSWI ? copyLinkForChoosenType(PdfTypes.FullTraining) :
                                                        sw.type === SWTypes.TLMSWI ? copyLinkForChoosenType(PdfTypes.Full) :
                                                            copyLinkForChoosenType(PdfTypes.Normal)
                                                },
                                            },
                                        (sw.type === SWTypes.SWI) ? ({
                                            key: "Copy Full Training PDF Link",
                                            label: "Copy Full Training PDF Link",
                                            icon: pdfIcon,
                                            onClick: () => copyLinkForChoosenType(PdfTypes.FullTraining),
                                        }) : undefined,
                                        (sw.type === SWTypes.TLMSWI) ? (
                                            {
                                                key: "Standard PDF",
                                                label: sw.templateType === TemplateTypes.Consolidated ? "Consolidated PDF" : "Standard PDF",
                                                icon: pdfIcon,
                                                onClick: () => {
                                                    sw.templateType === TemplateTypes.Consolidated ?
                                                        copyLinkForChoosenType(PdfTypes.Consolidated) :
                                                        copyLinkForChoosenType(PdfTypes.Standard)
                                                },
                                            }) : undefined,
                                        (sw.type === SWTypes.MFGSWI) ? ({
                                            key: "Reference PDF",
                                            label: "Reference PDF",
                                            icon: pdfIcon,
                                            onClick: () => copyLinkForChoosenType(PdfTypes.Reference),
                                        }) : undefined,
                                        (sw.type === SWTypes.TLMSWI) ?
                                            {
                                                key: "Executable PDF",
                                                label: "Executable PDF",
                                                icon: pdfIcon,
                                                onClick: () => copyLinkForChoosenType(PdfTypes.Executable),
                                            } : undefined,
                                        (sw.type === SWTypes.CL ||
                                            sw.type === SWTypes.LCL ||
                                            sw.type === SWTypes.SWI ||
                                            (sw.type === SWTypes.TLMRC && sw.rcType !== RCTypes.Notice) ||
                                            sw.type === SWTypes.TLMSWI ||
                                            sw.type === SWTypes.MFGSWI ||
                                            sw.type === SWTypes.MFGCL ||
                                            (sw.type === SWTypes.MFGRC && sw.rcType !== RCTypes.Notice)
                                        ) ? ({
                                            key: sw.type === SWTypes.TLMSWI || sw.type === SWTypes.TLMRC || sw.type === SWTypes.MFGRC ? "Competency - CPA" : "CCQ PDF",
                                            label: sw.type === SWTypes.TLMSWI || sw.type === SWTypes.TLMRC || sw.type === SWTypes.MFGRC ? "Competency - CPA" : "CCQ PDF",
                                            icon: pdfIcon,
                                            onClick: () => {
                                                sw.type === SWTypes.TLMSWI ||
                                                    sw.type === SWTypes.TLMRC ||
                                                    sw.type === SWTypes.MFGRC ? copyLinkForChoosenType(PdfTypes.CompetencyCPA) : copyLinkForChoosenType(PdfTypes.CCQ)
                                            },
                                        }) : undefined
                                    ])
                                    return (
                                        <EllipsesDropdown
                                            itemTextAlign="left"
                                            items={itemList}
                                        >
                                            <div>
                                                <img
                                                    src={shareLatestPdfLinkIcon}
                                                    className="icon-medium hover-image"
                                                    alt="Copy Latest PDF link" />
                                            </div>
                                        </EllipsesDropdown>
                                    )
                                }
                            })()}
                        </div>
                    </div >
                )}
        </>
    );
};

export default SWRowPDF;
